import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import BackendService from '../services/backendService';
import {
  SupportedIntegrationLabel,
  SupportedIntegrationResponse,
} from '../services/backendService/types';

const queryKey = ['integrations', SupportedIntegrationLabel.GoogleDrive];

const useGoogleDrive = (tenantId: string) => {
  const queryClient = useQueryClient();

  // Initialize react query for all integrations, with a refetch interval
  const googleDriveQuery = useQuery({
    queryKey,

    queryFn: async () => {
      // If we already have data with an access token, use refresh endpoint
      const existingData:
        | SupportedIntegrationResponse
        | undefined = queryClient.getQueryData(queryKey);
      if (existingData?.accessToken) {
        return await BackendService.instance.integrations.refreshMetadata(
          SupportedIntegrationLabel.GoogleDrive,
          tenantId,
        );
      }
      // Otherwise, get initial metadata
      return await BackendService.instance.integrations.getMetadata(
        SupportedIntegrationLabel.GoogleDrive,
        tenantId,
      );
    },

    refetchIntervalInBackground: true,
    refetchInterval: () => {
      const localUTC = new Date(new Date().toISOString());
      const existingData:
        | SupportedIntegrationResponse
        | undefined = queryClient.getQueryData(queryKey);
      const refreshIn = existingData?.accessToken?.expiresAt
        ? new Date(existingData.accessToken.expiresAt).getTime() -
          localUTC.getTime() -
          60000
        : 300000;
      // Ensure that we don't refresh with a negative (or 0) interval
      return Math.max(refreshIn, 1000);
    },
  });

  /**
   * Opens the consent popup and resolves once it closes, with whatever the
   * integration looks like afterwards — an `accessToken` if the teacher granted
   * access, nothing if they backed out.
   *
   * The promise used to be constructed without a `resolve`, so it never
   * settled: callers had no way to tell when auth had finished, which is why
   * the popup closing left the teacher to find the button again themselves.
   */
  const authenticate = useCallback(async (): Promise<
    SupportedIntegrationResponse | undefined
  > => {
    if (!googleDriveQuery.data || googleDriveQuery.isLoading || googleDriveQuery.error) {
      return undefined;
    }
    const authorizationUrl: string = googleDriveQuery.data.authorizationUrl;
    const authWindow = window.open(
      authorizationUrl,
      'Integration Authentication',
      'width=500,height=600',
    );
    if (!authWindow) {
      return undefined;
    }
    // Focus the window and wait for it to close
    authWindow.focus();
    await new Promise<void>(resolve => {
      const checkWindowClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindowClosed);
          resolve();
        }
      }, 1000);
    });
    const { data } = await googleDriveQuery.refetch();
    return data;
  }, [googleDriveQuery]);

  return {
    metadata: googleDriveQuery.data,
    refresh: googleDriveQuery.refetch,
    authenticate,
    isReady: googleDriveQuery.isSuccess,
    isLoading: googleDriveQuery.isLoading,
    isError: !!googleDriveQuery.error,
  };
};

export default useGoogleDrive;
